import React, {useState, useEffect} from 'react';
import {Elements, PaymentElement, useStripe, useElements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import {MdCheck} from 'react-icons/md';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Text,
	Box,
	VStack,
	Steps,
} from 'components/ui/chakra';
import DepositController from 'puddleglum/api/employer/DepositController';

interface DepositPaymentModalProps {
	isOpen: boolean;
	onClose: () => void;
	depositAmount: number;
}

const stripePromise = loadStripe((window as any).stripe_key);

function CheckoutForm({onBack}: {onBack: () => void}) {
	const stripe = useStripe();
	const elements = useElements();
	const [processing, setProcessing] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!stripe || !elements) return;

		setProcessing(true);
		const returnUrl: string = `${window.location.origin}/employer/onboarding/deposit/complete`;
		const result = await stripe.confirmPayment({
			elements,
			confirmParams: {
				return_url: returnUrl,
			},
		});

		if (result.error) {
			console.error(result.error);
			setProcessing(false);
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<Box mb={4}>
				<PaymentElement />
			</Box>
			<ModalFooter gap={3} px={0}>
				<Button variant='outline' onClick={onBack}>
					Back
				</Button>
				<Button type='submit' colorScheme='blue' isLoading={processing}>
					Pay Deposit
				</Button>
			</ModalFooter>
		</form>
	);
}

export default function DepositPaymentModal({
	isOpen,
	onClose,
	depositAmount,
}: DepositPaymentModalProps) {
	const [step, setStep] = useState(1);
	const [clientSecret, setClientSecret] = useState<string>();

	useEffect(() => {
		if (step === 2 && !clientSecret) {
			DepositController.createPaymentIntent()
				.then(({data}) => {
					setClientSecret(data.client_secret);
				})
				.catch(console.error);
		}
	}, [step]);

	const totalDeposit = depositAmount / 100;

	return (
		<Modal size='lg' isOpen={isOpen} onClose={onClose} isCentered>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Make Your Deposit</ModalHeader>
				<ModalBody>
					<VStack spacing={4} align='stretch'>
						<Steps.Root step={step - 1} size='sm' count={2}>
							<Steps.List>
								{[
									{
										title: 'Review',
										description: 'Review deposit amount',
									},
									{
										title: 'Payment',
										description: 'Enter payment details',
									},
								].map((item, index) => (
									<Steps.Item key={item.title} index={index}>
										<Steps.Indicator>
											<Steps.Status
												complete={<MdCheck />}
												current={<Steps.Number />}
												incomplete={<Steps.Number />}
											/>
										</Steps.Indicator>
										<Box flexShrink='0'>
											<Steps.Title>{item.title}</Steps.Title>
											<Steps.Description>
												{item.description}
											</Steps.Description>
										</Box>
										<Steps.Separator />
									</Steps.Item>
								))}
							</Steps.List>
						</Steps.Root>

						{step === 1 && (
							<Box>
								<Text fontSize='sm' color='gray.600'>
									Your initial deposit amount is ${totalDeposit.toFixed(2)}. This
									amount was calculated according to your number of employees.
									<br />
									<br />
									If you cancel your membership, this deposit will be refunded.
								</Text>
								<Button mt={4} colorScheme='blue' onClick={() => setStep(2)}>
									Continue to Payment
								</Button>
								<ModalFooter px={0}>
									<Button variant='outline' onClick={onClose}>
										Cancel
									</Button>
								</ModalFooter>
							</Box>
						)}

						{step === 2 && clientSecret && (
							<Box>
								<Text fontSize='lg' fontWeight='bold' mb={2}>
									Enter Payment Details
								</Text>
								<Elements
									stripe={stripePromise}
									options={{
										clientSecret,
										appearance: {theme: 'stripe'},
									}}
								>
									<CheckoutForm onBack={() => setStep(1)} />
								</Elements>
							</Box>
						)}
					</VStack>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}
